/**
 * AI Intelligence tab navigation (§8.7): the tab lives in the URL (?tab=) so links land where
 * they point. Settings is deliberately not a URL tab — it is a mode entered through the gear.
 * Extracted from the dashboard so the whitelist and URL handling are testable without
 * rendering the page.
 */

export const AI_INTEL_TABS = ["overview", "portfolio", "leaks", "attention", "inventory", "tiers", "respond", "graph", "usage"];

/** The tab a given location search resolves to — unknown and absent values land on Overview. */
export const resolveTabFromSearch = (search) => {
  const requested = new URLSearchParams(search).get("tab");
  return AI_INTEL_TABS.includes(requested) ? requested : "overview";
};

/** The tabs that serve org-wide data and so need ORG-level viewer access. */
const ORG_WIDE_TABS = ["portfolio", "leaks"];

/**
 * The tabs that serve the org-wide AI estate aggregates (summary, assets, action lists, graph,
 * usage). They follow `useRBAC().canViewAllAiEstateData`, which admits the platform-admin flags,
 * unlike ORG_WIDE_TABS above which stays on `canRunAgent`. `canViewEstate` defaults to true so
 * older call sites keep their meaning.
 */
const ESTATE_TABS = ["attention", "inventory", "tiers", "respond", "graph", "usage"];

/** The tabs that are AI Intelligence preview surfaces (Features:AiIntelPreview, served as aiIntelPreview). */
const PREVIEW_TABS = ["leaks"];

/**
 * The tab actually rendered. Portfolio and Data leaks are served only to ORG-level viewers and
 * above (both endpoints' CanViewAllOrganizationData; `useRBAC().canRunAgent` is the same rule), so
 * a shared `?tab=portfolio` or `?tab=leaks` link lands a caller without that access on Overview
 * rather than on a tab that would 403. `canViewOrgWide` is that access. A preview tab lands on
 * Overview the same way unless `preview` (useAiIntelPreview) is on, since its endpoint 404s.
 * Applied at render time, because roles can be empty on the first render.
 */
export const visibleTab = (tab, canViewOrgWide, preview, canViewEstate = true) =>
  (ORG_WIDE_TABS.includes(tab) && !canViewOrgWide) ||
  (ESTATE_TABS.includes(tab) && !canViewEstate) ||
  (PREVIEW_TABS.includes(tab) && !preview)
    ? "overview"
    : tab;

/** The URL to write when switching tabs; other query params (deep links) are preserved. */
export const tabUrl = (pathname, search, tab) => {
  const params = new URLSearchParams(search);
  params.set("tab", tab);
  return `${pathname}?${params.toString()}`;
};
